if (!customElements.get("quick-add")) {
  const QUICK_ADD_BG_HIDDEN_ATTR = "data-dbi-quick-add-bg-hidden";
  const QUICK_ADD_FOCUS_RETRY_DELAYS = [0, 16, 50, 100, 150, 250, 400, 500, 750, 1000, 1500];
  const quickAddOpenerStore = {
    button: null,
    quickAddHost: null,
  };

  function isFocusOnOpener(opener) {
    if (!opener) return false;

    const active = document.activeElement;
    if (!active) return false;
    if (active === opener || opener.contains(active)) return true;

    return false;
  }

  function isInvalidFocusDestination(element) {
    if (!element) return true;
    if (element === document.body || element === document.documentElement) return true;
    if (element.tagName === "IFRAME") return true;
    if (element.classList?.contains("skip-to-content-link")) return true;
    if (element.id === "root" && element.tagName === "MAIN") return true;

    return false;
  }

  function maintainFocusOnOpener(opener, onSettled) {
    if (!opener || !document.contains(opener)) {
      onSettled?.(false);
      return;
    }

    const reclaim = () => {
      if (!document.contains(opener)) return false;
      if (isFocusOnOpener(opener)) return true;

      try {
        opener.scrollIntoView({ block: "nearest", inline: "nearest" });
        opener.focus({ preventScroll: true });
      } catch {
        return false;
      }

      return isFocusOnOpener(opener);
    };

    let settled = false;
    const timers = [];

    const finish = (success) => {
      if (settled) return;
      settled = true;
      timers.forEach((timer) => clearTimeout(timer));
      document.removeEventListener("focusin", handleFocusIn, true);
      onSettled?.(success);
    };

    const handleFocusIn = (event) => {
      if (isFocusOnOpener(opener)) {
        finish(true);
        return;
      }

      if (isInvalidFocusDestination(event.target) && reclaim()) {
        finish(true);
      }
    };

    document.addEventListener("focusin", handleFocusIn, true);

    if (reclaim()) {
      finish(true);
      return;
    }

    QUICK_ADD_FOCUS_RETRY_DELAYS.forEach((delay) => {
      timers.push(
        setTimeout(() => {
          if (settled) return;
          if (reclaim()) finish(true);
        }, delay),
      );
    });

    timers.push(
      setTimeout(() => {
        finish(isFocusOnOpener(opener));
      }, QUICK_ADD_FOCUS_RETRY_DELAYS[QUICK_ADD_FOCUS_RETRY_DELAYS.length - 1] + 100),
    );
  }

  function rememberQuickAddOpener(button) {
    if (!(button instanceof HTMLElement)) return;

    document
      .querySelectorAll("[data-dbi-quick-add-trigger], [data-dbi-quick-add-opener]")
      .forEach((element) => {
        if (element !== button) {
          element.removeAttribute("data-dbi-quick-add-trigger");
          element.removeAttribute("data-dbi-quick-add-opener");
        }
      });

    quickAddOpenerStore.button = button;
    quickAddOpenerStore.quickAddHost = button.closest("quick-add");
    button.setAttribute("data-dbi-quick-add-trigger", "true");
    button.setAttribute("data-dbi-quick-add-opener", "true");
  }

  function getRememberedQuickAddOpener() {
    const markedOpener = document.querySelector("[data-dbi-quick-add-opener]");
    if (markedOpener instanceof HTMLElement) {
      return markedOpener;
    }

    const { button, quickAddHost } = quickAddOpenerStore;

    if (button && document.contains(button)) {
      return button;
    }

    if (quickAddHost && document.contains(quickAddHost)) {
      return quickAddHost.querySelector("button:not([disabled])");
    }

    return document.querySelector("[data-dbi-quick-add-trigger]");
  }

  function clearRememberedQuickAddOpener() {
    document
      .querySelectorAll("[data-dbi-quick-add-opener], [data-dbi-quick-add-trigger]")
      .forEach((element) => {
        element.removeAttribute("data-dbi-quick-add-opener");
        element.removeAttribute("data-dbi-quick-add-trigger");
      });

    quickAddOpenerStore.button = null;
    quickAddOpenerStore.quickAddHost = null;
  }

  function bindQuickAddOpenerMemory() {
    if (document.documentElement.dataset.dbiQuickAddOpenerMemory === "true") {
      return;
    }

    document.documentElement.dataset.dbiQuickAddOpenerMemory = "true";

    document.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("quick-add button");
        if (button) rememberQuickAddOpener(button);
      },
      true,
    );

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        const button = event.target.closest("quick-add button");
        if (button) rememberQuickAddOpener(button);
      },
      true,
    );
  }

  bindQuickAddOpenerMemory();

  function bindQuickViewCartCoordination() {
    if (document.documentElement.dataset.dbiQuickViewCartBound === "true") {
      return;
    }

    document.documentElement.dataset.dbiQuickViewCartBound = "true";

    document.addEventListener(PUB_SUB_EVENTS.cartDrawerOpen, () => {
      const instance = document.querySelector(".wt__quick-buy")?._activeQuickAdd;
      if (
        !instance?.isDrawerOpen ||
        !document.body.classList.contains("quick-view-modal-open")
      ) {
        return;
      }

      instance.ensureCartDrawerAccessible();
      if (typeof removeTrapFocus === "function") {
        removeTrapFocus();
      }
    });

    document.addEventListener(PUB_SUB_EVENTS.cartDrawerClose, () => {
      const instance = document.querySelector(".wt__quick-buy")?._activeQuickAdd;
      if (!instance?.isDrawerOpen) return;

      instance.cartDrawerOpenWhileQuickView = false;

      const returnButton = instance._cartDrawerReturnFocus;
      if (
        !returnButton ||
        !document.contains(returnButton) ||
        !document.body.classList.contains("quick-view-modal-open")
      ) {
        return;
      }

      const restore = () =>
        instance.restoreQuickViewFocusAfterCartClose(returnButton);

      if (restore()) return;

      [16, 50, 100, 150, 250, 400, 500, 750].forEach((delay) => {
        setTimeout(() => restore(), delay);
      });
    });
  }

  bindQuickViewCartCoordination();

  customElements.define(
    "quick-add",
    class QuickAdd extends HTMLElement {
      isFocusableElementVisible(element) {
        if (!element) return false;

        let node = element;
        while (node && node !== document.body) {
          if (node.getAttribute("aria-hidden") === "true") return false;
          if (node.hasAttribute("hidden")) return false;
          if (node.closest?.("[hidden], .hidden")) return false;
          node = node.parentElement;
        }

        return element.getClientRects().length > 0;
      }

      getFocusableElements(parent) {
        const selector =
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe, .wt-cart__drawer__close--button, .close-btn";
        const focusableElements = Array.from(
          parent.querySelectorAll(selector),
        ).filter(
          (el) =>
            !el.hasAttribute("disabled") &&
            el.tabIndex >= 0 &&
            this.isFocusableElementVisible(el),
        );

        return {
          focusableElements,
          first: focusableElements[0],
          last: focusableElements[focusableElements.length - 1],
        };
      }

      connectedCallback() {
        this.add_button = this.querySelector("button");
        this.isDrawerOpen = false;
        this.isMobile = window.innerWidth < 768;
          if (this.add_button)
            this.add_button.addEventListener("click", (e) => {
              const button = e.currentTarget;
              const quickAddEl = document.querySelector(".wt__quick-buy");
              rememberQuickAddOpener(button);
              if (quickAddEl) {
                quickAddEl._focusReturnTarget = button;
              }
              this.product_url = this.add_button.getAttribute("data-product-url");
              this.openerElement = button;
              this.activeTrigger = button;
              this.currentTrigger = button;
              this.markActiveTrigger(button);
              this.isQuickViewModal = Boolean(
                this.closest(".wt-featured-collection, collection-section"),
              );
              this.fetchProduct(this.product_url);
            });

        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.inertedElements = [];

        this.getFocusableElements = this.getFocusableElements.bind(this);

        this.lastFeaturedImage;
        this.firstMedia;
        this.hasFirstMediaImg;

        this.fetchProduct = this.fetchProduct.bind(this);
        this.galleryObserver = this.galleryObserver.bind(this);
        this.observerCallback = this.observerCallback.bind(this);
        this.disconnectObserver = this.disconnectObserver.bind(this);
        this.setupQuickViewModalLayout =
          this.setupQuickViewModalLayout.bind(this);
        this.setQuickViewAddToCartIcon =
          this.setQuickViewAddToCartIcon.bind(this);
      }

      setQuickViewAddToCartIcon(submitButton) {
        if (!submitButton) return;

        submitButton
          .querySelectorAll(".svg-icon, .card__quick-add-icon, .icon")
          .forEach((element) => element.remove());

        const iconTemplate = document.getElementById("quick-add-button-icon");
        const icon = iconTemplate?.content?.firstElementChild?.cloneNode(true);

        if (icon) {
          submitButton.insertBefore(icon, submitButton.firstChild);
        }

        const textSpan =
          submitButton.querySelector(".js-add-to-cart-text") ||
          submitButton.querySelector("span:not(.card__quick-add-icon)");

        if (textSpan) {
          textSpan.classList.add("js-add-to-cart-text");
          if (!submitButton.hasAttribute("disabled")) {
            const customLabel = submitButton.getAttribute("data-custom-add-label");
            textSpan.textContent =
              customLabel ||
              window.variantStrings?.addToCart ||
              "Add to cart";
          }
        }
      }

      setupQuickViewModalLayout(productGallery) {
        productGallery.style.display = "none";

        this.images = productGallery.querySelectorAll("img");
        const newVariantId =
          this.productCard.querySelector("variant-options");
        this.updateAttribute(
          productGallery,
          "id",
          `MediaGallery-${newVariantId?.getAttribute("data-section")}`,
        );

        const firstImageFromGallery = productGallery.querySelector("li");
        if (firstImageFromGallery) {
          this.firstMedia = firstImageFromGallery;

          this.playBtn = this.firstMedia.querySelector(".model-btn");
          if (this.playBtn) this.playBtn.setAttribute("tabindex", "0");

          this.video = this.firstMedia.querySelector("video");
          if (this.video) this.video.setAttribute("tabindex", "0");

          this.hasFirstMediaImg = Boolean(
            firstImageFromGallery.querySelector("img"),
          );
        }

        firstImageFromGallery
          ?.querySelector("img")
          ?.removeAttribute("srcset");

        const layout = document.createElement("div");
        layout.className = "quick-view-modal__layout";

        const media = document.createElement("div");
        media.className = "quick-view-modal__media product-image";
        const featuredImage = firstImageFromGallery?.querySelector("img");
        if (featuredImage) {
          const imageClone = featuredImage.cloneNode(true);
          imageClone.removeAttribute("srcset");
          media.appendChild(imageClone);
        }

        const content = document.createElement("div");
        content.className = "quick-view-modal__content";

        this.removeElements(
          this.productInfo,
          ".wt-product__brand",
          ".wt-rating",
          ".product__tax",
          ".wt-product__feature-tags",
          ".wt-separator",
          "collapsible-section",
          ".wt-product__features",
          ".complementary-products",
          ".wt-subscription-widget",
        );

        this.productInfo
          .querySelectorAll(".wt-product__text-block__title")
          .forEach((title) => title.remove());

        const title = this.productInfo.querySelector(".wt-product__name");
        if (title) {
          title.removeAttribute("tabindex");
          const titleLink = title.querySelector("a");
          if (titleLink) {
            title.textContent = titleLink.textContent;
          }
        }

        const sku = this.productInfo.querySelector(".wt-product__sku");
        if (sku) {
          const skuValue = sku.textContent.trim();
          sku.innerHTML = `<span class="quick-view-modal__sku-label">SKU:</span> ${skuValue}`;
        }

        const contentSelectors = [
          ".wt-product__name",
          ".wt-product__price",
          ".wt-product__text-block__wrapper",
          ".wt-description",
          ".wt-product__sku",
        ];

        contentSelectors.forEach((selector) => {
          this.productInfo.querySelectorAll(selector).forEach((element) => {
            content.appendChild(element);
          });
        });

        this.productInfo
          .querySelectorAll(".wt-product__options")
          .forEach((options) => {
            const blockWrapper =
              options.closest("[data-block-id]") || options.parentElement;
            if (blockWrapper) {
              content.appendChild(blockWrapper);
            }
          });

        let buyContainer = this.productInfo.querySelector(
          ".main-product__buy-buttons--container",
        );
        let productForm = buyContainer?.querySelector("product-form");

        if (!productForm) {
          productForm = this.productInfo.querySelector(
            "product-form.wt-product__add-to-cart",
          );
          buyContainer =
            productForm?.closest(".main-product__buy-buttons--container") ||
            productForm?.parentElement;
        }

        const counter =
          buyContainer?.querySelector("quantity-counter") ||
          productForm?.querySelector("quantity-counter") ||
          this.productInfo.querySelector("quantity-counter");
        let qtyRow = null;
        if (counter) {
          qtyRow = document.createElement("div");
          qtyRow.className = "quick-view-modal__qty";
          const qtyLabel = document.createElement("span");
          qtyLabel.className = "quick-view-modal__qty-label";
          qtyLabel.textContent = "QTY:";
          counter.parentElement.removeChild(counter);
          qtyRow.appendChild(qtyLabel);
          qtyRow.appendChild(counter);
        }

        if (buyContainer && productForm) {
          buyContainer.removeAttribute("style");

          const submitButton = productForm.querySelector('button[name="add"]');
          const actions = document.createElement("div");
          actions.className = "quick-view-modal__actions";

          const cartSlot = document.createElement("div");
          cartSlot.className = "quick-view-modal__action-slot quick-view-modal__action-slot--cart";

          const detailsSlot = document.createElement("div");
          detailsSlot.className =
            "quick-view-modal__action-slot quick-view-modal__action-slot--details";

          cartSlot.appendChild(productForm);
          buyContainer.remove();

          const viewDetails = document.createElement("a");
          viewDetails.href = this.product_url;
          viewDetails.className =
            "quick-view-modal__view-details hero__button hero__button--secondary";
          viewDetails.textContent =
            window.quickAddStrings?.viewProductDetails ||
            "View Product Details";
          detailsSlot.appendChild(viewDetails);

          actions.appendChild(cartSlot);
          actions.appendChild(detailsSlot);

          if (qtyRow) {
            content.appendChild(qtyRow);
          }

          content.appendChild(actions);

          const disclaimer = document.createElement("div");
          disclaimer.className = "quick-view-modal__disclaimer";
          const disclaimerText = document.createElement("p");
          disclaimerText.textContent =
            window.quickAddStrings?.disclaimer ||
            "* These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure or prevent any disease.";
          disclaimer.appendChild(disclaimerText);
          content.appendChild(disclaimer);

          if (submitButton) {
            submitButton.classList.remove("wt-button--full-width");
            this.setQuickViewAddToCartIcon(submitButton);
          }
        } else {
          if (qtyRow) {
            content.appendChild(qtyRow);
          }

          if (buyContainer) {
            content.appendChild(buyContainer);
          }
        }

        layout.appendChild(media);
        layout.appendChild(content);

        this.productCard.classList.add("quick-view-modal-product");
        this.productCard.innerHTML = "";
        this.productCard.appendChild(layout);
      }

      handleCloseClick() {
        this.keepOpenAfterAddToCart = false;

        const quickAdd = this.getActiveQuickAddElement();
        if (quickAdd) {
          quickAdd._focusReturnTarget =
            this.getTriggerForFocus(this) || quickAdd._focusReturnTarget;
        }

        this.fetchController?.abort();
        this.closeCart();
      }

      handleKeydown(e) {
        if (!this.isDrawerOpen) return;

        const isEsc =
          e.key === "Escape" || e.keyCode === 27 || e.code === "Escape";

        if (isEsc) {
          e.preventDefault();
          this.handleCloseClick();
          return;
        }

        if (
          !document.body.classList.contains("quick-view-modal-open") ||
          !this.quick_add_container
        ) {
          return;
        }

        const isTabPressed =
          e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
        if (!isTabPressed) return;

        const { first, last } = this.getFocusableElements(
          this.quick_add_container,
        );
        if (!first || !last) return;

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      getCloseButton(root = this.quick_add) {
        if (!root) return null;

        return root.querySelector(
          ".wt-cart__drawer__close--button, nav .close-btn, .close-btn",
        );
      }

      activateModalFocus(focusTarget = null) {
        const closeButton = this.getCloseButton();
        const container = this.quick_add_container;
        if (!closeButton || !container) return false;

        this.close_button = closeButton;
        closeButton.setAttribute("tabindex", "0");

        const elementToFocus = focusTarget || closeButton;

        if (typeof trapFocus === "function") {
          trapFocus(container, elementToFocus);
        } else {
          elementToFocus.focus({ preventScroll: true });
        }

        return (
          document.activeElement === elementToFocus ||
          elementToFocus.contains(document.activeElement)
        );
      }

      restoreQuickViewFocusAfterCartClose(returnButton) {
        if (
          !this.isDrawerOpen ||
          !document.body.classList.contains("quick-view-modal-open") ||
          !(returnButton instanceof HTMLElement) ||
          !document.contains(returnButton)
        ) {
          return false;
        }

        this.quick_add =
          this.quick_add || document.querySelector(".wt__quick-buy");
        this.quick_add_container =
          this.quick_add_container ||
          document.querySelector(".wt__quick-buy__container");

        if (!this.quick_add_container) return false;

        this.setBackgroundInert(true);

        const prepared = this.prepareTriggerForFocus(returnButton);
        if (!prepared) return false;

        return this.activateModalFocus(prepared);
      }

      deactivateModalFocus(trigger, onComplete) {
        const preparedTrigger = this.prepareTriggerForFocus(
          trigger || getRememberedQuickAddOpener(),
        );

        if (!preparedTrigger) {
          if (typeof removeTrapFocus === "function") {
            removeTrapFocus();
          }
          onComplete?.(false);
          return;
        }

        const closeButton = this.getCloseButton();
        closeButton?.setAttribute("tabindex", "-1");

        if (typeof removeTrapFocus === "function") {
          removeTrapFocus();
        }

        maintainFocusOnOpener(preparedTrigger, onComplete);
      }

      isFocusSuccessful(element) {
        return isFocusOnOpener(element);
      }

      getSavedFocusTarget(opener, quickAdd) {
        return (
          getRememberedQuickAddOpener() ||
          quickAdd?._focusReturnTarget ||
          opener?.openerElement ||
          document.querySelector("[data-dbi-quick-add-trigger]") ||
          opener?.activeTrigger ||
          opener?.add_button ||
          opener?.querySelector?.("button:not([disabled])") ||
          null
        );
      }

      getActiveQuickAddElement() {
        return document.querySelector(".wt__quick-buy");
      }

      getActiveOpener() {
        return this.getActiveQuickAddElement()?._activeQuickAdd || this;
      }

      markActiveTrigger(trigger) {
        rememberQuickAddOpener(trigger);
      }

      ensureSwiperSlideVisible(trigger) {
        const slide = trigger.closest(".swiper-slide, [data-swiper-slide]");
        if (!slide) return;

        const slideshow = trigger.closest("slideshow-section");
        const swiper = slideshow?.swiper;
        if (!swiper?.slides?.length) return;

        let slideIndex = Array.from(swiper.slides).indexOf(slide);
        if (slideIndex < 0) {
          slideIndex = Array.from(swiper.slides).findIndex((candidate) =>
            candidate.contains(slide),
          );
        }

        if (slideIndex >= 0 && swiper.activeIndex !== slideIndex) {
          swiper.slideTo(slideIndex, 0, false);
        }
      }

      getTriggerForFocus(opener = this.getActiveOpener()) {
        const quickAdd = this.getActiveQuickAddElement();
        const candidates = [
          getRememberedQuickAddOpener(),
          quickAdd?._focusReturnTarget,
          opener?.openerElement,
          document.querySelector("[data-dbi-quick-add-trigger]"),
          quickAdd?._activeTrigger,
          opener?.activeTrigger,
          opener?.currentTrigger,
          opener?.add_button,
          opener?.querySelector?.("button:not([disabled])"),
        ].filter(Boolean);

        for (const candidate of candidates) {
          if (!document.contains(candidate)) continue;
          if (this.isFocusableElementVisible(candidate)) return candidate;
        }

        return candidates.find((candidate) => document.contains(candidate)) || null;
      }

      prepareTriggerForFocus(trigger) {
        if (!trigger || !document.contains(trigger)) return null;

        const restoreTabindex = (element) => {
          if (element.hasAttribute("data-dbi-consent-prev-tabindex")) {
            const previous = element.getAttribute("data-dbi-consent-prev-tabindex");
            if (previous === "") element.removeAttribute("tabindex");
            else element.setAttribute("tabindex", previous);
            element.removeAttribute("data-dbi-consent-prev-tabindex");
          }
        };

        let node = trigger;
        while (node && node !== document.body) {
          node.inert = false;
          restoreTabindex(node);

          if (node.hasAttribute(QUICK_ADD_BG_HIDDEN_ATTR)) {
            node.removeAttribute("aria-hidden");
            node.removeAttribute(QUICK_ADD_BG_HIDDEN_ATTR);
          }

          if (node.hasAttribute("data-dbi-consent-bg-hidden")) {
            node.removeAttribute("aria-hidden");
            node.removeAttribute("data-dbi-consent-bg-hidden");
          }

          if (node.hasAttribute("aria-hidden") && node !== trigger) {
            node.removeAttribute("aria-hidden");
          }

          node = node.parentElement;
        }

        restoreTabindex(trigger);

        if (
          trigger.tagName === "BUTTON" &&
          trigger.getAttribute("tabindex") === "-1" &&
          !trigger.hasAttribute("data-dbi-consent-prev-tabindex")
        ) {
          trigger.removeAttribute("tabindex");
        }

        this.ensureSwiperSlideVisible(trigger);

        if (trigger.tabIndex < 0) {
          trigger.setAttribute("tabindex", "0");
        }

        return trigger;
      }

      restoreFocusToTrigger(trigger, onComplete) {
        const preparedTrigger = this.prepareTriggerForFocus(trigger);
        if (!preparedTrigger) {
          onComplete?.(false);
          return;
        }

        const isDisabled =
          preparedTrigger.hasAttribute("disabled") ||
          preparedTrigger.getAttribute("aria-disabled") === "true";
        if (isDisabled) {
          onComplete?.(false);
          return;
        }

        let restored = false;
        let focusRetryTimers = [];

        const clearFocusRetries = () => {
          focusRetryTimers.forEach((timer) => clearTimeout(timer));
          focusRetryTimers = [];
        };

        const focusTrigger = () => {
          try {
            preparedTrigger.scrollIntoView({
              block: "nearest",
              inline: "nearest",
            });
            preparedTrigger.focus({ preventScroll: true });
          } catch {
            return false;
          }

          return this.isFocusSuccessful(preparedTrigger);
        };

        const finish = (success) => {
          if (restored) return;
          restored = true;
          clearFocusRetries();
          onComplete?.(success);
        };

        const tryRestore = () => {
          if (restored) return;
          if (focusTrigger()) finish(true);
        };

        if (focusTrigger()) {
          finish(true);
          return;
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(tryRestore);
        });

        focusRetryTimers = QUICK_ADD_FOCUS_RETRY_DELAYS.map((delay) =>
          setTimeout(tryRestore, delay),
        );

        focusRetryTimers.push(
          setTimeout(() => {
            if (!restored) finish(false);
          }, QUICK_ADD_FOCUS_RETRY_DELAYS[QUICK_ADD_FOCUS_RETRY_DELAYS.length - 1] + 50),
        );
      }

      bindSharedCloseButtonHandlers() {
        if (document.documentElement.dataset.dbiQuickAddCloseBound === "true") {
          return;
        }

        document.documentElement.dataset.dbiQuickAddCloseBound = "true";

        document.addEventListener(
          "mousedown",
          (event) => {
            const closeButton = event.target.closest(
              ".wt__quick-buy .wt-cart__drawer__close--button, .wt__quick-buy nav .close-btn",
            );
            if (!closeButton) return;

            const quickAdd = document.querySelector(".wt__quick-buy");
            const opener = quickAdd?._activeQuickAdd;
            if (!quickAdd || !opener?.isDrawerOpen) return;

            event.preventDefault();
            quickAdd._focusReturnTarget =
              opener.getTriggerForFocus(opener) || quickAdd._focusReturnTarget;
          },
          true,
        );

        document.addEventListener(
          "click",
          (event) => {
            const closeButton = event.target.closest(
              ".wt__quick-buy .wt-cart__drawer__close--button, .wt__quick-buy nav .close-btn",
            );
            if (!closeButton) return;

            const quickAdd = document.querySelector(".wt__quick-buy");
            const opener = quickAdd?._activeQuickAdd;
            if (!quickAdd || !opener?.isDrawerOpen) return;

            event.preventDefault();
            quickAdd._focusReturnTarget =
              opener.getTriggerForFocus(opener) || quickAdd._focusReturnTarget;
            opener.handleCloseClick();
          },
          true,
        );

        document.addEventListener(
          "keydown",
          (event) => {
            const closeButton = event.target.closest(
              ".wt__quick-buy .wt-cart__drawer__close--button, .wt__quick-buy nav .close-btn",
            );
            if (!closeButton) return;
            if (event.key !== "Enter" && event.key !== " ") return;

            const quickAdd = document.querySelector(".wt__quick-buy");
            const opener = quickAdd?._activeQuickAdd;
            if (!quickAdd || !opener?.isDrawerOpen) return;

            event.preventDefault();
            quickAdd._focusReturnTarget =
              opener.getTriggerForFocus(opener) || quickAdd._focusReturnTarget;
            opener.handleCloseClick();
          },
          true,
        );
      }

      bindSharedModalHandlers() {
        this.bindSharedCloseButtonHandlers();

        if (
          !this.quick_add_container ||
          this.quick_add_container.dataset.dbiKeydownBound === "true"
        ) {
          return;
        }

        this.quick_add_container.dataset.dbiKeydownBound = "true";
        this.quick_add_container.addEventListener("keydown", (e) => {
          document.querySelector(".wt__quick-buy")?._activeQuickAdd?.handleKeydown(e);
        });
      }

      bindSharedOverlayHandler() {
        if (document.documentElement.dataset.dbiQuickAddOutsideBound === "true") {
          return;
        }

        document.documentElement.dataset.dbiQuickAddOutsideBound = "true";
        document.addEventListener("click", (event) => {
          const quickAdd = document.querySelector(".wt__quick-buy");
          const opener = quickAdd?._activeQuickAdd;
          const pageOverlay = document.querySelector(
            ".wt__quick-buy--page-overlay",
          );

          if (opener?.isDrawerOpen && event.target === pageOverlay) {
            opener.keepOpenAfterAddToCart = false;
            quickAdd._focusReturnTarget =
              opener.getTriggerForFocus(opener) || quickAdd._focusReturnTarget;
            opener.closeCart();
          }
        });
      }

      ensureCartDrawerAccessible() {
        const cartDrawer = document.querySelector("cart-drawer");
        if (!cartDrawer) return;

        cartDrawer.inert = false;
        cartDrawer.removeAttribute("aria-hidden");
        cartDrawer.removeAttribute(QUICK_ADD_BG_HIDDEN_ATTR);
      }

      prepareForCartDrawerOpen(returnFocusButton) {
        if (!(returnFocusButton instanceof HTMLElement)) return;

        this.cartDrawerOpenWhileQuickView = true;
        this._cartDrawerReturnFocus = returnFocusButton;
        this.ensureCartDrawerAccessible();

        if (typeof removeTrapFocus === "function") {
          removeTrapFocus();
        }
      }

      setBackgroundInert(isInert) {
        if (isInert) {
          this.inertedElements = [];

          Array.from(document.body.children).forEach((child) => {
            if (
              child.tagName === "SCRIPT" ||
              child.tagName === "STYLE" ||
              child.tagName === "CART-DRAWER" ||
              child === this.quick_add ||
              child === this.page_overlay ||
              child.classList?.contains("wt__quick-buy--page-overlay")
            ) {
              return;
            }

            child.inert = true;
            child.setAttribute(QUICK_ADD_BG_HIDDEN_ATTR, "true");
            child.setAttribute("aria-hidden", "true");
            this.inertedElements.push(child);
          });

          return;
        }

        this.inertedElements?.forEach((element) => {
          if (document.body.contains(element)) {
            element.inert = false;
            element.removeAttribute("aria-hidden");
            element.removeAttribute(QUICK_ADD_BG_HIDDEN_ATTR);
          }
        });
        this.inertedElements = [];

        const main = document.getElementById("root");
        if (main) {
          main.inert = false;
          if (main.getAttribute(QUICK_ADD_BG_HIDDEN_ATTR) === "true") {
            main.removeAttribute("aria-hidden");
            main.removeAttribute(QUICK_ADD_BG_HIDDEN_ATTR);
          }
        }
      }

      openModalAccessibility() {
        this.quick_add_container.setAttribute("role", "dialog");
        this.quick_add_container.setAttribute("aria-modal", "true");
        this.quick_add_container.setAttribute("tabindex", "-1");
        this.setBackgroundInert(true);
        this.bindSharedModalHandlers();
      }

      closeModalAccessibility() {
        this.quick_add_container.removeAttribute("role");
        this.quick_add_container.removeAttribute("aria-modal");
        this.quick_add_container.removeAttribute("aria-label");
        this.quick_add_container.removeAttribute("tabindex");
        this.setBackgroundInert(false);
      }

      fetchProduct(product_url) {
        this.lastFeaturedImage = null;
        this.firstMedia = null;
        this.hasFirstMediaImg = null;
        this.quick_add = document.querySelector(".wt__quick-buy");
        this.quick_add_container = document.querySelector(
          ".wt__quick-buy__container",
        );
        this.quick_add_wrapper = document.querySelector(
          ".wt__quick-buy--wrapper",
        );
        this.quick_add_product = this.quick_add.querySelector(
          ".wt__quick-buy--product",
        );
        this.close_button = this.getCloseButton(this.quick_add);
        this.loader = this.quick_add.querySelector(".wt__quick-buy-loader");
        this.page_overlay = document.querySelector(
          ".wt__quick-buy--page-overlay",
        );
        this.body = document.body;

        const controller = new AbortController();
        const signal = controller.signal;
        this.fetchController = controller;

        const allWtProducts =
          this.quick_add_wrapper.querySelectorAll(".wt-product");
        allWtProducts?.forEach((product) => product.remove());
        this.quick_add.classList.remove("hidden");
        this.body.classList.add("quick-buy-page-overlay");
        if (this.isQuickViewModal) {
          this.body.classList.add("quick-view-modal-open");
        }
        this.page_overlay.classList.add("wt__quick-buy--page-overlay--open");

        this.interactiveEelements = () =>
          this.quick_add_container.querySelectorAll();

        this.quick_add_container.classList.add(
          "wt__quick-buy__container--open",
        );

        this.close_button?.setAttribute("tabindex", "0");
        this.quick_add._activeQuickAdd = this;
        this.quick_add._activeTrigger = this.activeTrigger;
        this.quick_add._focusReturnTarget = this.openerElement || this.activeTrigger;
        this.markActiveTrigger(this.activeTrigger);

        this.isDrawerOpen = true;
        this.openModalAccessibility();
        this.bindSharedOverlayHandler();
        this.activateModalFocus();

        fetch(product_url, { signal })
          .then((res) => res.text())
          .then((res) => {
            this.loader.classList.add("hidden");
            const htmlDocument = new DOMParser().parseFromString(
              res,
              "text/html",
            );
            this.productMain = htmlDocument.querySelector(".wt-product__main");
            this.productCard = htmlDocument.querySelector(".wt-product");
            this.productInfo = htmlDocument.querySelector(".wt-product__info");

            const animatedElements = this.productMain.querySelectorAll(
              ".scroll-trigger.animate--slide-in",
            );
            animatedElements?.forEach((element) =>
              element.classList.remove("scroll-trigger", "animate--slide-in"),
            );

            const productGallery =
            this.productCard.querySelector("gallery-section");


            if (this.isQuickViewModal) {
              this.removeElements(
                this.productCard,
                "collapsible-section",
                ".product__inventory",
                ".share-icons__container",
                "#gallery-loader",
                "pickup-availability",
                ".wt-product__feature-icons",
                "gallery-section",
              );
            } else {
              this.removeElements(
                this.productCard,
                "collapsible-section",
                "[not-quick-add]",
                ".product__inventory",
                ".share-icons__container",
                "#gallery-loader",
                "pickup-availability",
                ".wt-product__sku",
                ".wt-product__feature-icons",
                "gallery-section",
              );
            }

            const newVariantId =
              this.productCard.querySelector("variant-options");
            if (newVariantId) {
              newVariantId.dataset.updateUrl = "false";
              this.updateAttribute(
                newVariantId,
                "data-original-section",
                newVariantId.getAttribute("data-section"),
              );
              this.updateAttribute(
                newVariantId,
                "data-section",
                `quick-${newVariantId.getAttribute("data-section")}`,
              );
            }

            if (this.isQuickViewModal) {
              this.setupQuickViewModalLayout(productGallery);
            } else {
              productGallery.style.display = "none";

              this.images = productGallery.querySelectorAll("img");
              this.updateAttribute(
                productGallery,
                "id",
                `MediaGallery-${newVariantId?.getAttribute("data-section")}`,
              );
              const firstImageFromGallery = productGallery.querySelector("li");
              if (firstImageFromGallery) {
                this.firstMedia = firstImageFromGallery;

                this.playBtn = this.firstMedia.querySelector(".model-btn");
                if (this.playBtn) this.playBtn.setAttribute("tabindex", "0");

                this.video = this.firstMedia.querySelector("video");
                if (this.video) this.video.setAttribute("tabindex", "0");

                this.hasFirstMediaImg = Boolean(
                  firstImageFromGallery.querySelector("img"),
                );
              }

              firstImageFromGallery
                ?.querySelector("img")
                ?.removeAttribute("srcset");
              const imageContainer = document.createElement("div");
              imageContainer.setAttribute("class", "product-image");

              const productDetails = document.createElement("div");
              productDetails.setAttribute("class", "wt-product__details");

              const productAbout = document.createElement("div");
              productAbout.setAttribute("class", "product-info");
              const title = this.productInfo.querySelector(".wt-product__name");

              if (title) {
                const aElement = document.createElement("a");
                aElement.textContent = title.textContent;
                aElement.href = this.product_url;
                title.innerHTML = "";
                title.appendChild(aElement);
                title.setAttribute("tabindex", "0");
              }

              const productElementsToCopy = this.productInfo.querySelectorAll(
                ".wt-product__brand, .wt-product__name, .wt-rating, .wt-product__price, .product__tax",
              );

              productElementsToCopy.forEach((product) =>
                productAbout.appendChild(product),
              );

              imageContainer.innerHTML = firstImageFromGallery?.innerHTML || "";
              productDetails.appendChild(imageContainer);
              productDetails.appendChild(productAbout);
              this.productInfo.prepend(productDetails);
            }

            this.galleryObserver();

            const mainProductPrice =
              this.productCard.querySelector(".wt-product__price");
            this.updateAttribute(
              mainProductPrice,
              "id",
              `price-${newVariantId?.getAttribute("data-section")}`,
            );

            const productFormElement = this.productCard.querySelector(
              'form[method="post"][data-type="add-to-cart-form"]',
            );
            const productFormId = `product-form-${newVariantId?.getAttribute("data-section")}`;
            this.updateAttribute(productFormElement, "id", productFormId);

            const allFieldSets = this.productCard.querySelectorAll("fieldset");
            this.updateFieldSets(allFieldSets, productFormId);

            const addBtn = this.productCard.querySelector('button[name="add"]');
            this.updateAttribute(
              addBtn,
              "id",
              `ProductSubmitButton-${newVariantId?.getAttribute("data-section")}`,
            );

            if (this.isQuickViewModal && productFormId) {
              const quantityInput = this.productCard.querySelector(
                '.quick-view-modal__qty input[name="quantity"]',
              );
              if (quantityInput) {
                quantityInput.setAttribute("form", productFormId);
              }
            }

            const subWidget = this.productCard.querySelector('.wt-subscription-widget');
            if (subWidget) {
              const quickSectionId = newVariantId?.getAttribute("data-section");
              const originalSectionId = newVariantId?.getAttribute("data-original-section");
              subWidget.setAttribute('data-section-id', quickSectionId);
              subWidget.setAttribute('data-original-section-id', originalSectionId);
              subWidget.setAttribute('data-update-url-on-plan-change', 'false');

              const hiddenInput = subWidget.querySelector('input[name="selling_plan"]');
              if (hiddenInput) {
                hiddenInput.setAttribute('form', `product-form-${quickSectionId}`);
              }
            }

            const viewAllDetailsContainer =
              this.quick_add_wrapper.querySelector(
                ".wt__quick-buy__view-all-container",
              );
            this.quick_add_wrapper.removeChild(viewAllDetailsContainer);
            this.quick_add_wrapper.innerHTML += this.productCard.outerHTML;
            this.quick_add_wrapper.append(viewAllDetailsContainer);

            const giftCardInput = this.quick_add_wrapper.querySelector(
              '[name="properties[__shopify_send_gift_card_to_recipient]"]',
            );
            if (giftCardInput) giftCardInput.removeAttribute("disabled");

            if (this.isQuickViewModal) {
              this.hideViewAllDetailsButton();
            } else {
              this.addViewAllDetailsButton();
            }

            // Fire the quick-buy drawer–open event
            publish(PUB_SUB_EVENTS.quickBuyDrawerOpen, { source: "quick-add" });

            // initialize the variant options
            const variantOptions = this.quick_add_wrapper.querySelector("variant-options");
            if (variantOptions && typeof variantOptions.initialize === 'function') {
              variantOptions.initialize();
            }

            if (this.isQuickViewModal) {
              const submitButton =
                this.quick_add_wrapper.querySelector('button[name="add"]');
              this.setQuickViewAddToCartIcon(submitButton);
            }

            const productTitle = this.quick_add_wrapper.querySelector(
              ".wt-product__name",
            );
            if (productTitle) {
              this.quick_add_container.setAttribute(
                "aria-label",
                productTitle.textContent.trim(),
              );
            }

            this.activateModalFocus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.error("Fetch aborted");
            } else {
              console.error("Fetch error:", error);
            }
          });
      }

      addViewAllDetailsButton() {
        const viewAllDetailsContainer = this.quick_add_wrapper.querySelector(
          ".wt__quick-buy__view-all-container",
        );
        const link = viewAllDetailsContainer.querySelector("a");
        link.href = this.product_url;
        link.setAttribute("tabindex", "0");
        viewAllDetailsContainer.classList.remove("hidden");
      }

      hideViewAllDetailsButton() {
        const viewAllDetailsContainer = this.quick_add_wrapper.querySelector(
          ".wt__quick-buy__view-all-container",
        );
        viewAllDetailsContainer.classList.add("hidden");
        viewAllDetailsContainer
          .querySelector("a")
          ?.setAttribute("tabindex", "-1");
      }

      galleryObserver() {
        const config = {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true,
        };
        this.observer = new MutationObserver(this.observerCallback);
        this.observer.observe(this.quick_add_wrapper, config);
      }

      observerCallback(mutations) {
        for (const mutation of mutations) {
          if (mutation.target.localName === "variant-options") {
            this.quick_add = document.querySelector(".wt__quick-buy");
            const variantOptions =
              this.quick_add.querySelector("variant-options");
            const imageContainer =
              this.quick_add.querySelector(".product-image");
            const image = imageContainer.querySelector("img");
            const formInput = this.quick_add.querySelector(
              'form input[name="id"]',
            );
            if (variantOptions.getAttribute("data-variant-id")) {
              formInput.value = variantOptions.getAttribute("data-variant-id");
              formInput.setAttribute(
                "value",
                variantOptions.getAttribute("data-variant-id"),
              );
            }
            if (
              variantOptions.getAttribute("data-featured-image") &&
              this.lastFeaturedImage !==
                variantOptions.getAttribute("data-featured-image")
            ) {
              image?.setAttribute(
                "src",
                variantOptions.getAttribute("data-featured-image"),
              );
              image?.removeAttribute("srcset");

              this.lastFeaturedImage = variantOptions.getAttribute(
                "data-featured-image",
              );
              if (
                imageContainer.children[0]?.nodeName !== "IMG" &&
                imageContainer.children[0]?.nodeName !== "A"
              ) {
                const newImage = document.createElement("img");
                newImage.setAttribute(
                  "class",
                  "wt-product__img  wt-product__img--zoom-cursor",
                );
                newImage.setAttribute(
                  "src",
                  variantOptions.getAttribute("data-featured-image"),
                );
                newImage.removeAttribute("srcset");
                imageContainer.innerHTML = "";
                imageContainer.appendChild(newImage);
              } else {
                image.setAttribute(
                  "src",
                  variantOptions.getAttribute("data-featured-image"),
                );
                image.removeAttribute("srcset");
              }
              this.lastFeaturedImage = variantOptions.getAttribute(
                "data-featured-image",
              );
            } else {
              const hasFirstMediaModel =
                this.firstMedia?.querySelector("model-element");
              if (!this.hasFirstMediaImg || hasFirstMediaModel)
                imageContainer.innerHTML = this.firstMedia?.innerHTML;
            }
            break;
          }
        }
      }

      disconnectObserver() {
        this.observer?.disconnect();
      }

      finalizeCloseState({
        quickAdd,
        opener,
        triggerToRestore,
        focusRestored = false,
      }) {
        const closeButton = this.getCloseButton(quickAdd);
        let restored = focusRestored;
        const trigger =
          triggerToRestore ||
          getRememberedQuickAddOpener() ||
          opener?.openerElement;

        if (!restored && trigger) {
          const preparedTrigger = this.prepareTriggerForFocus(trigger);
          if (preparedTrigger) {
            if (typeof removeTrapFocus === "function") {
              removeTrapFocus(preparedTrigger);
            } else {
              closeButton?.blur();
              preparedTrigger.focus({ preventScroll: true });
            }
            restored = this.isFocusSuccessful(preparedTrigger);
          }
        }

        closeButton?.setAttribute("tabindex", "-1");

        if (restored) {
          clearRememberedQuickAddOpener();
        }

        opener.openerElement = null;
        opener.activeTrigger = null;
        opener.currentTrigger = null;
        if (quickAdd) {
          delete quickAdd._activeQuickAdd;
          delete quickAdd._activeTrigger;
          delete quickAdd._focusReturnTarget;
        }
      }

      closeCart() {
        if (!this.isDrawerOpen) return;

        this.keepOpenAfterAddToCart = false;

        const quickAdd = this.getActiveQuickAddElement();
        const opener = quickAdd?._activeQuickAdd || this;
        const savedFocusTarget = this.getSavedFocusTarget(opener, quickAdd);

        // Fire the quick-buy drawer–close event
        publish(PUB_SUB_EVENTS.quickBuyDrawerClose, { source: "quick-add" });

        this.closeModalAccessibility();

        const loader = this.quick_add.querySelector(".wt__quick-buy-loader");
        loader.classList.remove("hidden");
        const product = this.quick_add.querySelector(".wt-product");

        if (product) product.remove();

        this.body.classList.remove("quick-buy-page-overlay");
        this.body.classList.remove("quick-view-modal-open");
        this.page_overlay.classList.remove("wt__quick-buy--page-overlay--open");
        this.quick_add_container.classList.remove(
          "wt__quick-buy__container--open",
        );
        this.isDrawerOpen = false;
        opener.isDrawerOpen = false;
        this.hideViewAllDetailsButton();
        this.disconnectObserver();

        const triggerToRestore =
          this.getTriggerForFocus(opener) || savedFocusTarget;

        const finalizeClose = (focusRestored) => {
          this.finalizeCloseState({
            quickAdd,
            opener,
            triggerToRestore,
            focusRestored,
          });
        };

        this.deactivateModalFocus(triggerToRestore, finalizeClose);
      }

      removeElements(element, ...selectors) {
        selectors.forEach((selector) => {
          const elements = element.querySelectorAll(selector);
          elements.forEach((el) => el.remove());
        });
      }

      updateAttribute(element, attribute, value) {
        if (element) {
          element.setAttribute(attribute, value);
        }
      }

      updateFieldSets(fieldSets, productFormId) {
        fieldSets.forEach((fieldset) => {
          const inputRadios = fieldset.querySelectorAll('input[type="radio"]');
          const labels = fieldset.querySelectorAll("label");

          inputRadios.forEach((radio) => {
            this.updateAttribute(
              radio,
              "id",
              `quick-${radio.getAttribute("id")}`,
            );
            if (productFormId) {
              this.updateAttribute(radio, "form", productFormId);
            }
          });

          labels.forEach((label) => {
            this.updateAttribute(
              label,
              "for",
              `quick-${label.getAttribute("for")}`,
            );
          });
        });
      }

      disconnectedCallback() {
        if (this.isDrawerOpen) {
          this.closeCart();
        } else {
          this.closeModalAccessibility();
        }
        this.disconnectObserver();
      }
    },
  );
}
